import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  QrCode,
  Eye,
  MapPin,
  TabletSmartphone,
  Search,
  Layers3,
  Flame,
  Globe,
  TrendingUp,
} from "lucide-react";

export default function MenoAnalytics() {
  const { t } = useTranslation();

  const stats = {
    qrScans: t("analytics.statsQrScans"),
    linkVisits: t("analytics.statsLinkVisits"),
  };

  const featurePoints = [
    {
      icon: ScanMonitoringIcon,
      titleKey: "analytics.f1.title",
      descriptionKey: "analytics.f1.desc",
    },
    {
      icon: GeographicInsightsIcon,
      titleKey: "analytics.f2.title",
      descriptionKey: "analytics.f2.desc",
    },
    {
      icon: DeviceAnalyticsIcon,
      titleKey: "analytics.f3.title",
      descriptionKey: "analytics.f3.desc",
    },
    {
      icon: TrafficSourceIcon,
      titleKey: "analytics.f4.title",
      descriptionKey: "analytics.f4.desc",
    },
  ];

  // Constant loop for the dashboard floating effect
  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const sectionReveal = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        staggerChildren: 0.12,
      },
    },
  };

  const itemReveal = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <section className="bg-white py-24 text-slate-900 font-sans antialiased overflow-hidden safari-paint-contain">
      <motion.div
        className="custom-container"
        variants={sectionReveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
      >
        {/* Header Section */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          variants={itemReveal}
        >
          <motion.div variants={itemReveal}>
            <span className="inline-flex items-center gap-2 bg-pink-50 text-pink-700 px-3 py-1 rounded-full text-xs font-semibold mb-4 border border-pink-100">
              <Layers3 aria-hidden="true" focusable="false" size={14} />{" "}
              {t("analytics.badge")}
            </span>
            <h2>
              <span
                className="text-3xl md:text-4xl lg:text-6xl font-bold tracking-tight"
                style={{
                  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                {t("analytics.title")}
              </span>
            </h2>
          </motion.div>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start pt-4 lg:pt-0"
          variants={itemReveal}
        >
          {/* LEFT SIDE: Features */}
          <motion.div
            className="lg:col-span-5 space-y-10"
            variants={itemReveal}
          >
            <motion.div className="md:pt-12" variants={itemReveal}>
              <h3 style={{ lineHeight: "1.5" }}>
                <span
                  className="text-lg md:text-xl font-bold"
                  style={{
                    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                  }}
                >
                  {t("analytics.lead")}
                </span>
              </h3>
              <div
                className="text-slate-500 text-base mt-2"
                style={{
                  fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                }}
              >
                {t("analytics.sub")}
              </div>
            </motion.div>
            <div className="space-y-8">
              {featurePoints.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-5"
                  variants={itemReveal}
                >
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex-shrink-0 flex items-center justify-center p-3 border border-slate-100">
                    <feature.icon />
                  </div>
                  <div>
                    <h4>
                      <span
                        className="text-lg font-bold"
                        style={{
                          fontFamily:
                            '"Inter", ui-sans-serif, system-ui, sans-serif',
                        }}
                      >
                        {t(feature.titleKey)}
                      </span>
                    </h4>
                    <div
                      className="text-slate-500 text-sm leading-relaxed"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {t(feature.descriptionKey)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT SIDE: Animated Dashboard Visual */}
          <motion.div
            className="lg:col-span-7 relative lg:pt-16"
            variants={itemReveal}
            role="img"
            aria-label={t("analytics.dashboardAlt")}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Card 1: Scanning Pulse Animation */}
              <motion.div
                variants={itemReveal}
                whileHover={{
                  scale: 1.05,
                  boxShadow:
                    "0 8px 18px rgba(147, 51, 234, 0.14), 0 2px 8px rgba(236, 72, 153, 0.12)",
                }}
                className="bg-[#f8fafc] p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4"
              >
                <div className="relative w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center">
                  <QrCode aria-hidden="true" focusable="false" size={24} />
                  <motion.div
                    animate={{ opacity: [0, 1, 0], scale: [1, 1.4, 1.8] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="absolute inset-0 bg-purple-200 rounded-xl -z-10"
                  />
                </div>
                <div>
                  <p
                    className="text-xs font-medium text-slate-400"
                    style={{
                      fontFamily:
                        '"Inter", ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    {t("analytics.qrScansLabel")}
                  </p>
                  <p
                    className="text-2xl font-bold"
                    style={{
                      fontFamily:
                        '"Inter", ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    {stats.qrScans}
                  </p>
                </div>
              </motion.div>

              {/* Card 2: Eye Blink/Fade Animation */}
              <motion.div
                variants={itemReveal}
                whileHover={{
                  scale: 1.05,
                  boxShadow:
                    "0 8px 18px rgba(13, 148, 136, 0.14), 0 2px 8px rgba(20, 184, 166, 0.12)",
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 20,
                }}
                className="bg-[#f8fafc] p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-teal-50 text-teal-600 rounded-xl flex items-center justify-center">
                  <motion.div
                    animate={{ opacity: [1, 0.4, 1] }}
                    transition={{ repeat: Infinity, duration: 3 }}
                  >
                    <Eye aria-hidden="true" focusable="false" size={24} />
                  </motion.div>
                </div>
                <div>
                  <p
                    className="text-xs font-medium text-slate-400"
                    style={{
                      fontFamily:
                        '"Inter", ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    {t("analytics.linkVisitsLabel")}
                  </p>
                  <p
                    className="text-2xl font-bold"
                    style={{
                      fontFamily:
                        '"Inter", ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    {stats.linkVisits}
                  </p>
                </div>
              </motion.div>

              {/* Geo Heatmap with Pulsing Hotspots */}
              <motion.div
                className="md:col-span-2 bg-[#f8fafc] rounded-3xl p-6 border border-slate-100 relative overflow-hidden h-64"
                variants={itemReveal}
              >
                <div className="flex justify-between items-center mb-4 relative z-10">
                  <span
                    className="text-sm font-bold flex items-center gap-2"
                    style={{
                      fontFamily:
                        '"Inter", ui-sans-serif, system-ui, sans-serif',
                    }}
                  >
                    <Globe
                      aria-hidden="true"
                      focusable="false"
                      size={16}
                      className="text-blue-500"
                    />{" "}
                    {t("analytics.visitorLocations")}
                  </span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-[10px] bg-white px-2 py-1 rounded-md shadow-sm font-bold uppercase">
                      {t("analytics.live")}
                    </span>
                  </div>
                </div>

                {/* Visual Map Background */}
                <svg
                  className="absolute inset-0 w-full h-full opacity-10 pointer-events-none"
                  viewBox="0 0 800 400"
                >
                  <path
                    d="M50 200 Q 200 100 400 200 T 750 200"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeDasharray="10 10"
                  />
                </svg>

                {/* Animated Map Hotspots */}
                {[
                  {
                    t: "20%",
                    l: "25%",
                    c: "bg-pink-500",
                    g: "bg-pink-500/30",
                    d: 0,
                  },
                  {
                    t: "60%",
                    l: "40%",
                    c: "bg-purple-500",
                    g: "bg-purple-500/30",
                    d: 1,
                  },
                  {
                    t: "30%",
                    l: "70%",
                    c: "bg-teal-500",
                    g: "bg-teal-500/30",
                    d: 0.5,
                  },
                  {
                    t: "75%",
                    l: "80%",
                    c: "bg-blue-500",
                    g: "bg-blue-500/30",
                    d: 1.5,
                  },
                ].map((dot, i) => (
                  <div
                    key={i}
                    className="absolute"
                    style={{ top: dot.t, left: dot.l }}
                  >
                    <motion.div
                      animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                      transition={{
                        repeat: Infinity,
                        duration: 2,
                        delay: dot.d,
                      }}
                      className={`w-6 h-6 ${dot.g} rounded-full blur-[2px]`}
                    />
                    <div
                      className={`absolute inset-1 w-4 h-4 ${dot.c} rounded-full border border-white`}
                    />
                  </div>
                ))}
              </motion.div>

              {/* Bottom Row: Item Performance with Shimmer */}
              <motion.div
                variants={itemReveal}
                className="bg-[#f8fafc] p-5 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden"
              >
                <p
                  className="text-xs font-bold text-slate-400 mb-4 flex items-center gap-1 uppercase tracking-wider"
                  style={{
                    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                  }}
                >
                  <motion.span
                    animate={{
                      scale: [1, 1.12, 0.95, 1.08, 1],
                      rotate: [0, -8, 6, -4, 0],
                      opacity: [0.8, 1, 0.9, 1, 0.85],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                    className="inline-flex"
                  >
                    <Flame
                      aria-hidden="true"
                      focusable="false"
                      size={12}
                      className="text-orange-500 drop-shadow-[0_0_6px_rgba(249,115,22,0.8)]"
                    />
                  </motion.span>{" "}
                  {t("analytics.topItem")}
                </p>
                <div className="flex items-center gap-3">
                  <motion.div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center text-xl">
                    🌮
                  </motion.div>
                  <div>
                    <p
                      className="font-bold text-sm"
                      style={{
                        fontFamily:
                          '"Inter", ui-sans-serif, system-ui, sans-serif',
                      }}
                    >
                      {t("analytics.topItemName")}
                    </p>
                    <div className="flex items-center gap-1 text-green-500 font-bold text-xs">
                      <TrendingUp
                        aria-hidden="true"
                        focusable="false"
                        size={12}
                      />{" "}
                      {t("analytics.growth")}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Device Split with Growing/Shrinking Bar */}
              <motion.div
                variants={itemReveal}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.3,
                }}
                className="bg-[#f8fafc] p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-center"
              >
                <p
                  className="text-xs font-bold text-slate-400 mb-3 uppercase tracking-wider"
                  style={{
                    fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif',
                  }}
                >
                  {t("analytics.liveTraffic")}
                </p>
                <div className="space-y-3">
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <motion.div
                      animate={{ width: ["60%", "85%", "60%"] }}
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-bold text-slate-500 uppercase">
                    <span className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />{" "}
                      {t("analytics.mobile")}
                    </span>
                    <span>{t("analytics.desktop")}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Global Style for the slow spin */}
      <style>{`
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </section>
  );
}

// Icons
const ScanMonitoringIcon = () => (
  <div className="relative">
    <QrCode
      aria-hidden="true"
      focusable="false"
      className="w-full h-full text-slate-700"
      strokeWidth={1.5}
    />
    <motion.div
      animate={{ opacity: [0, 1, 0] }}
      transition={{ repeat: Infinity, duration: 1.5 }}
      className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-pink-500 border-2 border-white"
    />
  </div>
);
const GeographicInsightsIcon = () => (
  <MapPin
    aria-hidden="true"
    focusable="false"
    className="w-full h-full text-pink-600"
    strokeWidth={1.5}
  />
);
const DeviceAnalyticsIcon = () => (
  <TabletSmartphone
    aria-hidden="true"
    focusable="false"
    className="w-full h-full text-purple-600"
    strokeWidth={1.5}
  />
);
const TrafficSourceIcon = () => (
  <Search
    aria-hidden="true"
    focusable="false"
    className="w-full h-full text-teal-600"
    strokeWidth={1.5}
  />
);
